import {
  add,
  addHours,
  addMinutes,
  addSeconds,
  differenceInDays,
  differenceInMinutes,
  endOfDay,
  format,
  formatDuration,
  fromUnixTime,
  intervalToDuration,
  minutesToHours,
  parse,
  setDay,
  startOfDay,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import intlFormat from 'date-fns/intlFormat';
import { DateTime, TimeUnit, TimeZone } from 'timezonecomplete';

import type { IBoundaryBlocks } from '@/api/types';
import type { IWeek, IWeekDay } from '@/types/calendar';

import i18n from '@/config/i18n';
import {
  AM_PM_TIME_SPACE_FORMAT,
  BLOCKS_IN_HOUR,
  HOUR_IN_PX,
  HOURS_IN_DAY,
  MILLIS_IN_SECOND,
  MINUTES_IN_HOUR,
  MONTH_DAY_FORMAT,
  MONTH_DAY_HOUR_FORMAT,
  MONTH_DAY_YEAR_FORMAT,
  SECONDS_IN_BLOCK,
  SECONDS_IN_MINUTE,
  TIMESTAMP_ISO_REGEX,
  TIMEZONE_ISO_REGEX,
  YEAR_MONTH_DAY_TIME,
  YEAR_MONTH_DAY_TIME_FORMAT,
} from '@/config/variables';
import { CalendarDisplayRange, WeekDays } from '@/store/enums';
import { CachedBlock } from '@/classes/cachedBlock';
import { differenceInWeeks } from 'date-fns';
import { ILxcDateTime } from '@/store/types';

const formatDate = (date: Date, dateFormat = YEAR_MONTH_DAY_TIME): string => {
  const result = format(date, dateFormat);
  return result.toString();
};

const formatTimestamp = (timestamp: number, dateFormat = YEAR_MONTH_DAY_TIME): string => {
  const date = fromUnixTime(timestamp);
  const result = format(date, dateFormat);
  return result.toString();
};

const convertMinutesFromMidnightToPX = (date: Date): number => {
  const hourOffset = date.getHours() * HOUR_IN_PX;
  return hourOffset + HOUR_IN_PX * (date.getMinutes() / 60);
};

// TODO remove in LVXC-1364
const getUnixTimestamp = (blockId: number): number => blockId * SECONDS_IN_BLOCK * 1000;

// TODO replace usage with IBlock method `.startOfDay()`
const getStartBlockOfDay = (blockId: number, tz: string): number => {
  const dateTime = new DateTime(getUnixTimestamp(blockId), TimeZone.utc()).convert(TimeZone.zone(tz));
  const startOfDay = dateTime.startOfDay();
  return CachedBlock.getBlockIdByMillis(startOfDay.unixUtcMillis());
};

const convertStartBlockToDayOffset = (startBlock: number, tz: string, day?: IWeekDay): number => {
  const startBlockOfDay = day?.startBlock ?? getStartBlockOfDay(startBlock, tz);
  return ((startBlock - startBlockOfDay) / BLOCKS_IN_HOUR) * HOUR_IN_PX;
};

const getHoursInDay = (day: IWeekDay, week: IWeek, selectedDays: CalendarDisplayRange): number => {
  if (selectedDays === CalendarDisplayRange.ONE_DAY && day.isDlsDay && week.dst?.tzOffsetDiff) {
    return HOURS_IN_DAY - week.dst.tzOffsetDiff / BLOCKS_IN_HOUR;
  }
  return HOURS_IN_DAY;
};

const getTotal = (start: Date, end: Date, count = 1): string => {
  const diffInMinutes = count * differenceInMinutes(end, start);
  const minutesLeft = diffInMinutes % 60;
  const hours = minutesToHours(diffInMinutes - minutesLeft);
  return minutesLeft ? `${hours}h ${minutesLeft}min` : `${hours}h`;
};

const getTotalFromMinutes = (minutes: number): string => {
  const minutesLeft = minutes % 60;
  const hours = minutesToHours(minutes);
  return minutesLeft ? `${hours}h ${minutesLeft}min` : `${hours}h`;
};

const year = new Date().getFullYear();
const getYears = (num: number): number[] => Array.from(new Array(num), (_, index) => year - index);
const getMonths = (): string[] => {
  return [
    i18n.t(`common.months.january`),
    i18n.t(`common.months.february`),
    i18n.t(`common.months.march`),
    i18n.t(`common.months.april`),
    i18n.t(`common.months.may`),
    i18n.t(`common.months.june`),
    i18n.t(`common.months.july`),
    i18n.t(`common.months.august`),
    i18n.t(`common.months.september`),
    i18n.t(`common.months.october`),
    i18n.t(`common.months.november`),
    i18n.t(`common.months.december`),
  ];
};

const getDatesForMonth = (): number[] => Array.from(new Array(31), (_, index) => 1 + index);

/**
 * Initialize a JavaScript Date to a particular time zone.
 *
 * @param {Date} originalDate
 * @param {string} timeZone
 * @return {*}  {Date}
 */
const dateWithTimeZone = (originalDate: Date, timeZone: string): Date => {
  const date = new Date(
    Date.UTC(
      originalDate.getFullYear(),
      originalDate.getMonth(),
      originalDate.getDate(),
      originalDate.getHours(),
      originalDate.getMinutes(),
      originalDate.getSeconds(),
    ),
  );
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timeZone }));
  const offset = utcDate.getTime() - tzDate.getTime();

  date.setTime(date.getTime() + offset);

  return date;
};

const dateWithTimeZoneFromString = (
  dateAsString: string,
  timeZone: string,
  parseSchema = YEAR_MONTH_DAY_TIME_FORMAT,
): Date => parse(dateAsString, parseSchema, dateWithTimeZone(new Date(), timeZone));

/**
 * @param weekId id of week to get the date for
 * @param day day of the week, 0 is Monday
 * @returns date for `weekId` if `weekId` is non-zero,
 * otherwise returns beginning of current week
 */
const getDateFromWeekId = (weekId: number, day = WeekDays.Monday): Date => {
  // date will be set to the beggining of the week
  const date = weekId ? new Date(1970, 0, 1) : new Date();
  return setDay(add(date, { weeks: weekId }), day + 1);
};

const getZonedDateFromWeekId = (weekId: number, tz: string, day = WeekDays.Monday): Date => {
  const weekStart = new DateTime(0, TimeZone.utc()).add(weekId, TimeUnit.Week).convert(TimeZone.zone(tz));
  return weekStart
    .sub(weekStart.weekDay() + day - 1, TimeUnit.Day)
    .startOfDay()
    .toDate();
};

const daysToMillis = (days: number): number =>
  days * HOURS_IN_DAY * MINUTES_IN_HOUR * SECONDS_IN_MINUTE * MILLIS_IN_SECOND;

const getZonedWeekdayEnd = (weekId: number, tz: string): Date => {
  let date = getZonedDateFromWeekId(weekId, tz, 7);
  date = addHours(date, 23);
  date = addMinutes(date, 59);
  date = addSeconds(date, 59);
  return date;
};

const getDatesFromWeekId = (weekId: number): Date[] => {
  //get all days in a week from week id
  const date = weekId ? new Date(1970, 0, 1) : new Date();
  return [1, 2, 3, 4, 5, 6, 7].map(day => setDay(add(date, { weeks: weekId }), day));
};

// LXC
const getBlockFromDate = (date: Date): number => Math.trunc(Number(date.getTime() / 1800 / 1000));
const convertBlockToDate = (blockId: number, timeZone?: string): Date => {
  /**
     For default retrieval date from block inside app's component, please use
     the method "getDateFromBlock" imported from useBlockDictionary hook
     */

  const timestamp = blockId * 1800 * 1000;
  if (!timeZone) {
    return new Date(timestamp);
  }

  const tempDate = new Date(1970, 0, 1, 0, 0, 0, 0);
  const tempDateTZ = dateWithTimeZone(tempDate, timeZone);
  const offset = new Date(tempDateTZ).getHours() - new Date(tempDate).getHours();

  const date = new Date(timestamp);
  date.setHours(date.getHours() - offset);
  return date;
};

const getDateByTimeZone = (epochDateinSeconds: number, dateFormat: string, timeZone: string): string => {
  let date = '';
  switch (dateFormat) {
    case MONTH_DAY_HOUR_FORMAT:
      date = intlFormat(
        epochDateinSeconds * 1000,
        {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          timeZone: timeZone,
          hour12: true,
        },
        { locale: 'en-US' },
      );
      break;
    case AM_PM_TIME_SPACE_FORMAT:
      date = intlFormat(
        epochDateinSeconds * 1000,
        {
          hour: 'numeric',
          minute: 'numeric',
          timeZone: timeZone,
          hour12: true,
        },
        { locale: 'en-US' },
      );
      break;
    case MONTH_DAY_FORMAT:
      date = intlFormat(
        epochDateinSeconds * 1000,
        {
          month: 'short',
          day: 'numeric',
          timeZone: timeZone,
          hour12: true,
        },
        { locale: 'en-US' },
      );
      break;
    case MONTH_DAY_YEAR_FORMAT:
      date = intlFormat(
        epochDateinSeconds * 1000,
        {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: timeZone,
          hour12: true,
        },
        { locale: 'en-US' },
      );
      break;
    default:
      date = new Date(epochDateinSeconds * 1000).toLocaleString('en-US', {
        timeZone: timeZone,
      });
      break;
  }

  return date;
};

const convertWeekdayIndexIntoName = (weekdayIndex: WeekDays): string => {
  switch (weekdayIndex) {
    case WeekDays.Monday:
      return i18n.t`common.weekdays.monday`;
    case WeekDays.Tuesday:
      return i18n.t`common.weekdays.tuesday`;
    case WeekDays.Wednesday:
      return i18n.t`common.weekdays.wednesday`;
    case WeekDays.Thursday:
      return i18n.t`common.weekdays.thursday`;
    case WeekDays.Friday:
      return i18n.t`common.weekdays.friday`;
    case WeekDays.Saturday:
      return i18n.t`common.weekdays.saturday`;
    case WeekDays.Sunday:
      return i18n.t`common.weekdays.sunday`;
    default:
      return '';
  }
};

const convertWeekdayIndexIntoShortName = (weekdayIndex: number): string => {
  switch (weekdayIndex) {
    case 0:
      return i18n.t`common.weekdaysShort.monday`;
    case 1:
      return i18n.t`common.weekdaysShort.tuesday`;
    case 2:
      return i18n.t`common.weekdaysShort.wednesday`;
    case 3:
      return i18n.t`common.weekdaysShort.thursday`;
    case 4:
      return i18n.t`common.weekdaysShort.friday`;
    case 5:
      return i18n.t`common.weekdaysShort.saturday`;
    case 6:
      return i18n.t`common.weekdaysShort.sunday`;
    default:
      return '';
  }
};

const convertWeekdayShortNameIntoIndex = (dayName: string): number => {
  switch (dayName) {
    case i18n.t`common.weekdaysShort.monday`:
      return 0;
    case i18n.t`common.weekdaysShort.tuesday`:
      return 1;
    case i18n.t`common.weekdaysShort.wednesday`:
      return 2;
    case i18n.t`common.weekdaysShort.thursday`:
      return 3;
    case i18n.t`common.weekdaysShort.friday`:
      return 4;
    case i18n.t`common.weekdaysShort.saturday`:
      return 5;
    case i18n.t`common.weekdaysShort.sunday`:
      return 6;
    default:
      return 0;
  }
};

const translateDayToCalendarIndex = (dayIndex: number): number => {
  switch (dayIndex) {
    case 1:
      return 0; // monday
    case 2:
      return 1;
    case 3:
      return 2;
    case 4:
      return 3;
    case 5:
      return 4;
    case 6:
      return 5;
    default:
      return 6; // sunday
  }
};

/**
 * Read "weekId" from the URL query param.
 *
 * @return {*}  {(number | null)}
 */
const getWeekIdFromURL = (): number | null => {
  const query = new URLSearchParams(location.search);
  const queryParam = query.get('weekid');
  return queryParam ? Number(queryParam) : null;
};

const formatDateToUtc = (date: Date): Date =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ),
  );

const getBoundaryBlocksForDate = (startDate: Date, endDate: Date = startDate): IBoundaryBlocks => {
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);
  return { startBlock: getBlockFromDate(start), endBlock: getBlockFromDate(end) };
};

const getUTCBoundaryBlocks = (start: Date, end: Date = start): IBoundaryBlocks => {
  const startUTC = startOfDay(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
  const endUTC = endOfDay(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));

  return { startBlock: getBlockFromDate(startUTC), endBlock: getBlockFromDate(endUTC) };
};

const getWeekdayFromTimestamp = (timestamp: string, timeZone: string): string =>
  new Date(timestamp).toLocaleString('default', {
    weekday: 'long',
    timeZone: timeZone,
  });

const getMonthFromTimestamp = (timestamp: string): string =>
  new Date(timestamp).toLocaleString('default', { month: 'long' });

const getTimeFromTimestamp = (timestamp: string, timezone: string): string =>
  new Date(timestamp + 'Z').toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: timezone,
  });

const getZonedTime = (date: Date, timeZone: string): string => {
  return date.toLocaleString('en-US', { timeZone: timeZone });
};

const getLxcDateTime = (dateString: string, timeZone: string): ILxcDateTime => {
  if (!dateString) return getDefaultLxcDateTime(timeZone);
  const formats = [
    'yyyy-MM-dd',
    'MM/dd/yyyy',
    'MM/dd/yyyy hh:mm a',
    'dd-MM-yyyy',
    'yyyy.MM.dd',
    'MMM dd, yyyy',
    'MMMM dd, yyyy',
    'yyyy/MM/dd',
    'yyyyMMdd',
  ];
  let date = new Date(dateString);
  for (const format of formats) {
    const parsedDate = parse(dateString, format, new Date());
    if (!isNaN(parsedDate.getTime())) {
      date = parsedDate;
    }
  }
  return getLxcDateTimeFromDateTime(date, timeZone);
};

const getLxcDateTimeOnlyDate = (dateString: string, timeZone: string): ILxcDateTime => {
  const lxcDateTime = getLxcDateTime(dateString, timeZone);
  lxcDateTime.hour = 0;
  lxcDateTime.minute = 0;
  lxcDateTime.second = 0;
  lxcDateTime.ampm = 'AM';
  lxcDateTime.hourHalfDayFormat = 0;
  return lxcDateTime;
};

const getLxcDateTimeFromDateTime = (date: Date, timeZone: string): ILxcDateTime => {
  if (!date) return getDefaultLxcDateTime(timeZone);
  const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
  let hoursHalfDayFormat = date.getHours() % 12;
  hoursHalfDayFormat = hoursHalfDayFormat ? hoursHalfDayFormat : 12;
  const lxcDateTime: ILxcDateTime = {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
    timeZone: timeZone,
    ampm: ampm,
    hourHalfDayFormat: hoursHalfDayFormat,
  };
  return lxcDateTime;
};

const getDefaultLxcDateTime = (timeZone: string): ILxcDateTime => {
  return {
    year: 1970,
    month: 1,
    day: 1,
    hour: 0,
    minute: 0,
    second: 0,
    timeZone: timeZone,
    ampm: 'AM',
    hourHalfDayFormat: 0,
  };
};

const getTodayLxcDateTimeOnlyDate = (timeZone: string): ILxcDateTime => {
  const date = toZonedTime(new Date(), timeZone);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return getLxcDateTimeFromDateTime(dateOnly, timeZone);
};

const getLocalDateTimeFromLxcDateTime = (lxcDateTime: ILxcDateTime | undefined): Date => {
  if (!lxcDateTime) return new Date();
  return new Date(
    lxcDateTime.year,
    lxcDateTime.month - 1,
    lxcDateTime.day,
    lxcDateTime.hour,
    lxcDateTime.minute,
    lxcDateTime.second,
  );
};

const getLocalDateStringFromLxcDateTime = (lxcDateTime: ILxcDateTime | undefined, dateFormat: string): string => {
  if (!lxcDateTime) return '';
  const date = getLocalDateTimeFromLxcDateTime(lxcDateTime);
  return format(date, dateFormat);
};

const getUnixTimestampFromDateString = (dateString: string, timeZone: string): number => {
  const date = fromZonedTime(dateString, timeZone);
  return Math.floor(date.getTime() / 1000);
};

const getFormatDateStringFromUnixTimeStamp = (timestamp: number, dateFormat: string, timeZone: string): string => {
  if (timestamp <= 0) return '';
  const zonedDate = toZonedTime(timestamp * 1000, timeZone);
  return format(zonedDate, dateFormat);
};

const zonedTimeToUtcTime = (date: Date, timeZone: string): string => fromZonedTime(date, timeZone).toISOString();

const getLocaleDateWithOptions = (date: Date, options: Intl.DateTimeFormatOptions): string => {
  return date.toLocaleString('en-us', options);
};
const combineDateTimeToISO = (date: string, time: string, timeZone: string): string =>
  fromZonedTime(`${date} ${time}`, timeZone).toISOString();
const isTimestampISO = (date: string): boolean => TIMESTAMP_ISO_REGEX.test(date);
const isTimezoneAwareISO = (date: string): boolean => TIMESTAMP_ISO_REGEX.test(date) && TIMEZONE_ISO_REGEX.test(date);

const ensureTimezoneAwareness = (date: string): string =>
  !isTimestampISO(date) ? date : isTimezoneAwareISO(date) ? date : date + 'Z';

const getDateTimeFromTimestamp = (timestamp: string, timezone: string = TimeZone.utc().name()): string => {
  return new Date(timestamp + 'Z').toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: timezone,
  });
};

const getTimestampFromDateString = (date: string): number => {
  return new DateTime(date).unixUtcMillis();
};

const unixSecondstimeAgo = (timeStamp: number): string => {
  const nowUtc = Math.floor(new Date().getTime() / 1000);
  const diffTime = nowUtc - timeStamp;
  if (diffTime < 120) {
    return `${Math.floor(diffTime / 60)} min ago `;
  } else if (diffTime < 3600) {
    return `${Math.floor(diffTime / 60)} mins ago `;
  } else if (diffTime < 7200) {
    return `${Math.floor(diffTime / 3600)} hour ago`;
  } else if (diffTime < 86400) {
    return `${Math.floor(diffTime / 3600)} hours ago`;
  } else if (diffTime < 172800) {
    return `${Math.floor(diffTime / 86400)} day ago`;
  } else {
    return `${Math.floor(diffTime / 86400)} days ago`;
  }
};

const getDateAndTimeFromTimeStamp = (timestamp: string, timezone: string = TimeZone.utc().name()): number =>
  new Date(
    new Date(timestamp + 'Z').toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone,
    }),
  ).getTime();

const getFormatTimeZoneDate = (date: number, formatDate: string, timeZone: string): string => {
  const newDate = new Date(date * 1000).toLocaleString('en-US', { timeZone });
  return format(new Date(newDate), formatDate);
};

const formatWithMinutes = (d: Date): string => (d.getMinutes() === 30 ? 'h:mm' : 'h') + 'aaa';

const formatStartEndTime = (startDate: Date, endDate: Date): string => {
  return `${formatDate(startDate, formatWithMinutes(startDate))} - ${formatDate(endDate, formatWithMinutes(endDate))}`;
};

function convertMonthstoWeeksAndDays(start: number | Date, end: number | Date) {
  const weeks = differenceInWeeks(start, end);
  let days = differenceInDays(start, end);

  days = days - weeks * 7;

  return { weeks: weeks, days: days };
}

const getDateInterval = (start: number | Date, end: number | Date, delimiter = ', '): string => {
  const units = ['years', 'months', 'weeks', 'days', 'hours', 'minutes'];
  const duration = intervalToDuration({
    start: start || new Date(),
    end: end || new Date(),
  });

  if (duration.months) {
    const { weeks = 0, days = 0 } = convertMonthstoWeeksAndDays(end, start);
    duration.weeks = weeks;
    duration.days = days;
    duration.months = 0;
    duration.hours = 0;
    duration.minutes = 0;
  }

  const formatedDuration = formatDuration(duration, {
    format: units,
    delimiter,
  });
  return formatedDuration;
};
const getCountDown = (
  targetDate: number,
): { totalSeconds: number; days: number; hours: number; minutes: number; seconds: number } => {
  const countDown = targetDate - Math.floor(new Date().getTime() / 1000);
  const days =
    countDown > 0 ? Math.floor(countDown / (60 * 60 * 24)) : 0 - Math.floor((0 - countDown) / (60 * 60 * 24));
  const hours =
    countDown > 0
      ? Math.floor((countDown % (60 * 60 * 24)) / (60 * 60))
      : 0 - Math.floor(((0 - countDown) % (60 * 60 * 24)) / (60 * 60));
  const minutes =
    countDown > 0 ? Math.floor((countDown % (60 * 60)) / 60) : 0 - Math.floor(((0 - countDown) % (60 * 60)) / 60);
  const seconds = countDown > 0 ? Math.floor(countDown % 60) : 0 - Math.floor((0 - countDown) % 60);
  return { totalSeconds: countDown, days: days, hours: hours, minutes: minutes, seconds: seconds };
};

const getUtcSeconds = (date: Date): number => date.getTime() / 1000;

const diferrenceBetweenDates = (Date1: Date, Date2: Date): string => {
  const difference = differenceInDays(Date1, Date2);
  if (difference === 0) return `less than a day`;
  if (difference > 365) return 'more than a year ago';
  return `${difference} days ago`;
};

const getTimeHalfDayFormat = (time?: ILxcDateTime) => {
  if (!time) return '';
  const minuteFormat = Number(time.minute) < 10 ? '0' + time.minute : time.minute;
  return `${time.hourHalfDayFormat}:${minuteFormat} ${time.ampm}`;
};

const getCurrentUnixTimestamp = () => Math.floor(Date.now() / 1000);

export {
  getUTCBoundaryBlocks,
  getBoundaryBlocksForDate,
  convertBlockToDate,
  convertMinutesFromMidnightToPX,
  formatDate,
  formatTimestamp,
  dateWithTimeZone,
  dateWithTimeZoneFromString,
  getDateFromWeekId,
  getDatesFromWeekId,
  getBlockFromDate,
  getTotal,
  getTotalFromMinutes,
  convertWeekdayIndexIntoName,
  convertWeekdayIndexIntoShortName,
  convertWeekdayShortNameIntoIndex,
  translateDayToCalendarIndex,
  getMonths,
  getYears,
  formatDateToUtc,
  getWeekdayFromTimestamp,
  getMonthFromTimestamp,
  getTimeFromTimestamp,
  getTimestampFromDateString,
  getZonedTime,
  zonedTimeToUtcTime,
  getLocaleDateWithOptions,
  getDatesForMonth,
  combineDateTimeToISO,
  isTimestampISO,
  isTimezoneAwareISO,
  ensureTimezoneAwareness,
  getDateByTimeZone,
  getDateTimeFromTimestamp,
  getDateAndTimeFromTimeStamp,
  getZonedDateFromWeekId,
  getZonedWeekdayEnd,
  getWeekIdFromURL,
  convertStartBlockToDayOffset,
  getHoursInDay,
  getStartBlockOfDay,
  getFormatTimeZoneDate,
  formatWithMinutes,
  formatStartEndTime,
  unixSecondstimeAgo,
  daysToMillis,
  getDateInterval,
  getCountDown,
  getUtcSeconds,
  diferrenceBetweenDates,
  getTimeHalfDayFormat,
  getLxcDateTime,
  getLxcDateTimeOnlyDate,
  getLxcDateTimeFromDateTime,
  getLocalDateTimeFromLxcDateTime,
  getTodayLxcDateTimeOnlyDate,
  getLocalDateStringFromLxcDateTime,
  getUnixTimestampFromDateString,
  getFormatDateStringFromUnixTimeStamp,
  getCurrentUnixTimestamp,
};
